import { computePosition, offset, arrow, type Placement, autoUpdate } from "@floating-ui/dom"

interface TooltipOptions {
  idName: string,
  className: string,
  theme?: string,
  content: string,
  maxWidth: string,
  position: Placement,
  alignX: string;
}


class Tooltip {
  private element: HTMLElement;
  private tip: HTMLElement;
  private arrowEl: HTMLElement;
  private inner: HTMLElement;
  private disabled: boolean;
  private content?: string;
  private opts: TooltipOptions;
  private isActive: boolean;
  private title: string;
  private static defaults: TooltipOptions = {
    idName: '',
    className: '',
    content: '[title]',
    maxWidth: '420px',
    position: 'top-start',
    alignX: 'right',
  };

  constructor(elm: HTMLElement, options?: Partial<TooltipOptions>) {
    this.element = elm;
    this.opts = { ...Tooltip.defaults, ...options };
    this.disabled = false;
    this.isActive = false;
    this.title = ''

    // Create tooltip DOM elements
    const idNameHtml = this.opts.idName ? `id="${this.opts.idName}"` : '';
    const tipHtml = `<div ${idNameHtml} class="ap-tip${this.opts.theme ? ' ap-tip-' + this.opts.theme : ''} ${this.opts.className}">
                          <div class="ap-tip__inner tip-bg-image"></div>
                          <div class="ap-tip__arrow"></div>
                       </div>`;

    const template = document.createElement('div');
    template.innerHTML = tipHtml.trim();
    this.tip = template.firstElementChild as HTMLElement;
    document.body.appendChild(this.tip);

    this.arrowEl = this.tip.querySelector<HTMLElement>('.ap-tip__arrow')!;
    this.inner = this.tip.querySelector<HTMLElement>('.ap-tip__inner')!;

    this.init();
  }

  private init (): void {
    // Store the title attribute's value
    const title = this.element.getAttribute('title');
    if (this.opts.maxWidth) {
      this.tip.style.maxWidth = this.opts.maxWidth;
    }

    if (title) {
      this.title = title;
      this.element.dataset['aptip'] = 'true';

      // Bind events for showing and hiding the tooltip
      this.element.addEventListener('mouseenter', this.mouseenter.bind(this));
      this.element.addEventListener('mouseleave', this.mouseleave.bind(this));
    }
  }

  private mouseenter (): void {
    if (this.disabled) return;
    this.element.setAttribute('title', '');
    this.show();
  }

  private mouseleave (): void {
    if (this.disabled) return;

    if (!this.isActive) {
      const title = this.title;
      if (title) {
        this.element.setAttribute('title', title);
      }
    }
    this.hide();
  }

  private show (): void {
    if (this.disabled || this.isActive) return;

    this.reset();
    this.update();

    autoUpdate(this.element, this.tip, () => {
      computePosition(this.element, this.tip, {
        placement: this.opts.position,
        middleware: [
          // offset({
          //   alignmentAxis: -10,
          //   crossAxis: 0
          // }),
          arrow({ element: this.arrowEl })
        ],
      }
      ).then((pos) => {
        this.tip.setAttribute('data-popper-placement', pos.placement)
        // Adjust for viewport overflow

        // top
        if (this.opts.position.indexOf('top') !== -1) {
          this.tip.style.left = `${pos.x - 8}px`;
          this.tip.style.top = `${pos.y - 8}px`;
          if (pos.middlewareData) {
            if (pos.middlewareData.arrow) {
              const { x, y } = pos.middlewareData.arrow;
              Object.assign(this.arrowEl.style, {
                left: x != null ? `${x + 7}px` : '',
                top: y != null ? `${y}px` : '',
              });
            }

          }
        }

        // right
        if (this.opts.position.indexOf('right') !== -1) {
          this.tip.style.left = `${pos.x + 15}px`;
          this.tip.style.top = `${pos.y}px`;
          if (pos.middlewareData) {
            if (pos.middlewareData.arrow) {
              const { x, y } = pos.middlewareData.arrow;
              Object.assign(this.arrowEl.style, {
                left: x != null ? `${x - 10}px` : '',
                top: y != null ? `${y}px` : '',
              });
            }
          }
        }

      });

    }, { animationFrame: true })

    this.display();
  }

  private hide (): void {
    if (this.disabled || !this.isActive) return;
    this.display(true);
  }

  private reset (): void { }

  private display (hide = false): void {
    const active = this.isActive;
    if ((active && !hide) || (!active && hide)) return;

    if (hide) {
      this.tip.style.display = 'none';
    } else {
      this.tip.style.display = 'block';
    }
    this.isActive = !active;
  }

  private update (content?: string): void {
    if (this.disabled) return;

    if (content) {
      this.opts.content = content;
    } else {
      content = this.opts.content;
    }

    const newContent = content === '[title]' ? this.title : content;
    if (this.content !== newContent) {
      this.inner.innerHTML = newContent || '';
      // @ts-ignore: 2322
      this.content = newContent;
    }
  }

  public disable (): void {
    this.disabled = true;
  }

  public enable (): void {
    this.disabled = false;
  }

  public destroy (): void {
    this.reset();
    this.tip.remove();
  }
}

// Convert this to a plugin-like pattern as in jQuery
export const aptip = (elements: NodeListOf<HTMLElement>, options?: Partial<TooltipOptions>): void => {

  elements.forEach(elm => {
    if (elm.dataset['aptip']) {
      return;
    }
    const theme = elm.dataset['theme'] ?? 'white';
    const placement = (elm.dataset['placement'] ?? 'top-start') as Placement;
    const opt = { ...options, theme: theme, position: placement };

    new Tooltip(elm, opt)
  });
};

// Usage Example
//aptip(document.querySelectorAll('.tooltip-trigger'), { position: 'top', alignX: 'right' });
